import { Player } from '@lottiefiles/react-lottie-player';
import { navigate } from 'gatsby';
import { min } from 'ramda';
import React, { FC, SetStateAction, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTimer } from 'use-timer';

import BasicText from '@ui/atoms/BasicText';
import Button, { IButtonProps } from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import FlexRow, { IFlexRow } from '@ui/atoms/FlexRow';
import SubheadingText from '@ui/atoms/SubheadingText';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { IPageProps } from '@folx-types/flow-steps';

import PageLoader from '@assets/lottie/PageLoading.json';

import theme from '@theme/FolxTheme';

export interface IMembershipCheckPageContent {
	pageHeader: string;
	pageInfo: string;
	pageFineprint: string;
	helpLink: string;
	helpHref: string;
}

interface IMembershipCheckProps extends IPageProps {
	fetchMembership: () => void;
	fetchOnboarding: () => void;
	loading: boolean;
	onComplete: boolean;
	onReturn: string;
	pageContent: IMembershipCheckPageContent;
	setOnComplete: () => SetStateAction<boolean>;
	active?: boolean;
	error?: boolean;
}

const timerIncrement = 5;
const maxTimer = 15;

const SpacedColumn = spaced<IFlexColumn>(FlexColumn);
const SpacedButton = spaced<IButtonProps>(Button);
const AlignedRow = aligned<IFlexRow>(fullWidth<IFlexRow>(FlexRow));

const LoaderRow = styled(AlignedRow)`
	height: ${theme.spacing.xxxxl};
`;

const HelpButton = styled(SpacedButton)`
	flex-grow: 1;
	max-width: 35rem;
`;

const MembershipCheck: FC<IMembershipCheckProps> = ({
	active = false,
	error = false,
	fetchMembership = () => {},
	fetchOnboarding = () => {},
	loading,
	onComplete,
	onReturn,
	pageContent,
	setOnComplete = () => {},
}) => {
	const [refetchTimer, setRefetchTimer] = useState(0);

	const { pageHeader, pageInfo, pageFineprint, helpLink, helpHref } = pageContent;

	const { time, start, status } = useTimer({
		initialTime: 0,
		endTime: refetchTimer,
		onTimeOver: () => {
			setOnComplete(false);
			fetchMembership();
			fetchOnboarding();
		},
	});

	const refetchQueries = () => {
		setRefetchTimer(min(timerIncrement + refetchTimer, maxTimer));
		start();
	};

	useEffect(() => {
		if (loading || !onComplete) {
			return;
		}

		if (active) {
			navigate(onReturn, { state: { returned: true } });
			return;
		}

		if (!error) {
			refetchQueries();
			return;
		}
	}, [active, error, loading, onComplete]);

	useEffect(() => {
		if (!error) {
			return;
		}

		refetchQueries();
	}, [error]);

	return (
		<CenteredGridItem>
			<SpacedColumn gap="sm" marginBottom="lg">
				<SubheadingText>{pageHeader}</SubheadingText>
				<BasicText>{pageInfo}</BasicText>
			</SpacedColumn>
			<SpacedColumn gap="lg" marginBottom="xxl">
				<LoaderRow justifyContent="center" alignItems="center">
					{status === 'RUNNING' && (
						<BasicText variant="button">Retrying in {refetchTimer - time}</BasicText>
					)}
					{status !== 'RUNNING' && <Player autoplay loop src={PageLoader} />}
				</LoaderRow>
				{refetchTimer > timerIncrement && (
					<BasicText variant="fineprint">{pageFineprint}</BasicText>
				)}
			</SpacedColumn>
			<AlignedRow justifyContent="center">
				<HelpButton marginRight="lg" marginLeft="lg" label={helpLink} href={helpHref} />
			</AlignedRow>
		</CenteredGridItem>
	);
};

export default MembershipCheck;
